import * as React from 'react';
import { AddPhotoAlternateOutlined } from "@mui/icons-material";
import FlexboxContainerComponent from "components/UI/FlexboxContainer";
import LoadingIndicator from "components/UI/LoadingIndicator";
import { renderToast, toast } from "components/UI/ToastNotification";
import { useAuth } from "lib/Auth";
import { useState } from "react";
import { postMedia } from "requests/media";
import { Main } from "styles/themeColors";
import FileUploader from "view/FileUploader";
import Brand, * as Br from 'data/Brand';
import styled from "styled-components";
import { Color } from "lib/styles";
import Vendor from "data/Vendor";
import { BRAND_CONSTANTS, BRAND_TYPE } from 'constants/brands';
import BrandType from './BrandTypeChip';
import FeatureFlag from 'data/FeatureFlag';
import { useReadPermission } from 'hooks/useUserRoles';
import { PermissionCode } from 'data/UserPermissions';

const Logo = styled.img`
  margin: 90px 0 0;
  height: 400px;
  width: 400px;
  object-fit: contain;
  object-position: center;
  max-width: 300px;
  position: absolute;
`;

const SupportedFormat = styled.p`
  font-size: 12px;
  line-height: 1.67;
  color: ${Color.dimGray};
  padding: 0;
  margin: 0;
`;

const ErrorMessage = styled.p`
  color: ${Color.error};
  font-size: 12px;
  margin-top: 2px;
`;

const FileUploadContainer = styled.div`
    margin: 10px 0;
    text-align: 'center';
`;

const Tag = styled.div<any>`
    align-self: flex-start;
    margin-top: 10px;
    z-index: 100;
`;

const renderLogo = (
  media: Br.Logo,
  _index: number,
) => {
  return (
    <Logo src={process.env.REACT_APP_MEDIA_URL + media.path} />
  );
};

const FILE_SIZE_LIMIT_MB = 20;
const FILE_SIZE_LIMIT_BYTES = FILE_SIZE_LIMIT_MB * 1024 * 1024;

interface BrandFileUploadProps {
  brand: Brand;
  setBrand: (brand: Brand) => void;
  selectedVendor: Vendor | null;
  hasEditRights?: boolean;
  featureFlags: FeatureFlag;
  error?: string;
}

const BrandFileUpload = (props: BrandFileUploadProps) => {
  const { brand, setBrand, selectedVendor, hasEditRights, featureFlags, error } = props;
  const [uploading, setUploading] = useState(false);
  const { token } = useAuth();

  const fileUploadContainer = <FileUploadContainer>
    <FileUploader
      acceptTypes={['image/svg+xml']}
      onChange={async (file) => await uploadImage(file as File)}
      text={
        BRAND_CONSTANTS.UPLOAD_LOGO
      }
      borderColor={Main.brandDark}
      backgroundColor={Main.brandDark}
      color={Main.white}
      brandRedesignFlag
      disabled={uploading || !hasEditRights}
      permissionCode={PermissionCode.BRANDS_UPDATE_LOGO}
    />

    <div style={{ marginTop: 10, textAlign: 'center' }}>
      <SupportedFormat>{BRAND_CONSTANTS.SUPPORTED_FORMAT}</SupportedFormat>
      <SupportedFormat>{BRAND_CONSTANTS.MAX_20_MB}</SupportedFormat>
    </div>
  </FileUploadContainer>

  const uploadImage = async (file: File) => {
    // Check file type
    if (file.type !== 'image/svg+xml') {
      renderToast(toast.TYPE.ERROR, BRAND_CONSTANTS.INVALID_FILE_TYPE_ERROR);
      return;
    }

    // Check file size
    if (file.size > FILE_SIZE_LIMIT_BYTES) {
      renderToast(toast.TYPE.ERROR, `Error in ${file.name}. File size exceeds the limit of ${FILE_SIZE_LIMIT_MB}MB`);
      return;
    }

    setUploading(true);
    const image = await postMedia({
      file,
      token: token as string,
      type: 'vendor_logo',
      vendorId: selectedVendor ? selectedVendor.id : undefined,
    });

    setUploading(false);
    setBrand({ ...brand, media: [image] });
  };

  const showError = !!error && !brand.media?.[0]?.id;

  return (
    <FlexboxContainerComponent flexDirection='column' width='30%' margin='40px 50px 0 0' alignItems='center'>
      <FlexboxContainerComponent
        width="100%"
        alignItems='center'
        flexDirection='column'
        border={`1px solid ${showError ? Color.error : Color.lightGray}`}
        height='400px'
      >
        {
          !featureFlags?.businessUnit?.enabled &&
          <Tag>
            <BrandType type={brand.isParentBrand ? BRAND_TYPE.PARENT_BRAND : BRAND_TYPE.BRAND} />
          </Tag>
        }
        <FlexboxContainerComponent
          justifyContent='center'
          flexDirection='column'
          alignItems='center'
          margin='120px 0px 0px 0px'
        >
          {
            uploading && (
              <LoadingIndicator />
            )
          }
          {!uploading && (
            (brand.media && brand.media.length > 0 && brand.media[0]?.path)
              ? renderLogo(brand.media[0], -1)
              : <>
                <AddPhotoAlternateOutlined style={{ fontSize: 36, alignSelf: 'center' }} />
                {fileUploadContainer}
              </>
          )}
        </FlexboxContainerComponent>
      </FlexboxContainerComponent>
      {showError && <ErrorMessage>{error}</ErrorMessage>}
      {
        (brand.media && brand.media.length > 0 && brand.media[0]?.path) && !uploading &&
        <div style={{ marginTop: 20 }}>
          {fileUploadContainer}
        </div>
      }
    </FlexboxContainerComponent>);
}

export default BrandFileUpload;